import { Box, Button, Card, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CardSkeleton from "../../../shared/components/CardSkeleton";
import { useLazyGetPaginatedReservationsQuery } from "../../../app/store/api/reservationApi";
import ReservationCard from "../../../shared/components/ReservationCard";
import { FILTERS, getStatus, TRANSLATION_MAP } from "../../../shared/utils/reservation";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import FilterChip from "../../../shared/components/FilterChip";
import FilterMenu from "../../../shared/components/FilterMenu";

const ReservationsList = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [getPaginatedReservations, { data: reservationsData, isLoading: isQueryLoading }] = useLazyGetPaginatedReservationsQuery();
  const { reservations } = reservationsData || {};
  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [tokenData, setTokenData] = useState({ 1: { token: null } });
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("New");

  const filtered = useMemo(() => {
    if (!reservations) return [];
    if (filter === "All") return reservations;
    return reservations.filter((b) => getStatus(b.status) === filter);
  }, [filter, reservations]);

  const filterChipData = useMemo(() => {
    if (!reservations) return FILTERS.map((f) => ({ ...f, count: 0 }));
    return FILTERS.map((f) => ({
      ...f,
      count: f.key === "All" ? reservations.length : reservations.filter((b) => getStatus(b.status) === f.key).length,
    }));
  }, [reservations]);

  const handleCardClick = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const showSkeleton = isQueryLoading;

  useEffect(() => {
    if (!reservations && !showSkeleton) {
      getPaginatedReservations({ limit, nextToken: null });
    }
  }, [reservations, showSkeleton, getPaginatedReservations, limit]);

  const handleButtonClick = (direction) => {
    window.scrollTo(0, 0);
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
      setTokenData((previous) => ({
        ...previous,
        [currentPage + 1]: { token: reservationsData?.pagination?.nextToken },
      }));
      getPaginatedReservations({ limit, nextToken: reservationsData?.pagination?.nextToken });
    } else {
      setCurrentPage((prev) => prev - 1);
      const previousToken = tokenData[currentPage - 1]?.token || null;
      getPaginatedReservations({ limit, nextToken: previousToken });
    }
  };

  return (
    <>
      <Box>
        <Stack direction='row' spacing={1.25} sx={{ mb: 3, flexWrap: "wrap" }}>
          {isMobile ? (
            <FilterMenu options={filterChipData} setFilter={setFilter} filter={filter} />
          ) : (
            filterChipData.map((f) => <FilterChip key={f.key} label={t(`userDashboard.${TRANSLATION_MAP[f.key]}`)} count={f.count} active={filter === f.key} onClick={() => setFilter(f.key)} />)
          )}
        </Stack>
        {showSkeleton ? (
          <CardSkeleton />
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((b, index) => (
              <ReservationCard key={b.PK} reservation={b} index={index} expanded={expanded === b.PK} handleCardClick={handleCardClick} adminView={true} />
            ))}
            {filtered.length === 0 && (
              <Card elevation={0} sx={{ p: 4, textAlign: "center", border: "1px dashed #E3E7EE" }}>
                <Typography color='text.secondary'>{t("adminDashboard.noReservations")}</Typography>
              </Card>
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button variant='outlined' onClick={() => handleButtonClick("previous")} disabled={currentPage === 1}>
          {t("adminDashboard.previous")}
        </Button>
        <Button variant='outlined' onClick={() => handleButtonClick("next")} disabled={!reservationsData?.pagination?.hasMore}>
          {t("userDashboard.next")}
        </Button>
      </Box>
    </>
  );
};

export default ReservationsList;
